import Data from 'data';
import Locale from 'data/locale';
import { isSubType, presetItems } from './itemTypes';
import { isEmbed, Quality } from 'common';
import { gemStats, itemGetTypes, itemsetPartStats, itemsetStats } from './item';
import { clone, memoizedCompute } from 'utils';
import { d2_addpercent, AuraFlags } from './skills/calc';
import skillFunctions from './skills/functions';
import { selectMonster } from './monster/picker';
import monsterStats from './monster/stats';
import StatObject from './statObject';
import { profileSelectSkills } from './stateReducers';
import { summonStats } from './monster/summons';
import shrineBuffs from './skills/shrines';

function applyAuras(auras, stats, unit, team, source, info) {
  function addAura(unit, state, level, data) {
    if (!auras[unit]) auras[unit] = {};
    let key = state;
    const info = Data.states[state];
    if (info) {
      if (info.group) key = `group${info.group}`;
      if (info.curse && stats[unit].base.curse_resistance >= 100) return;
    }
    if (!auras[unit][key] || auras[unit][key].priority > data.priority || (auras[unit][key].priority === data.priority && auras[unit][key].level < level)) {
      auras[unit][key] = {
        state,
        level,
        source,
        ...data
      };
    }
  }
  
  if (info.state && unit) {
    addAura(unit, info.state, info.level, {stats: info.stats, priority: info.priority || 0, itemtype: info.itemtype});
  }
  if (info.filter && info.targetState) {
    for (const targetId in stats) {
      if (unit === targetId) continue;
      const target = stats[targetId];
      if (target.untargetable) continue;
      if (!(info.filter & target.aura_include) || (info.filter & target.aura_exclude)) continue;
      if ((info.filter & AuraFlags.TargetAllies) && target.team !== team) continue;
      if ((info.filter & AuraFlags.IgnoreAllies) && target.team === team) continue;
      if (info.targetCheck && !info.targetCheck(targetId, target)) continue;
      addAura(targetId, info.targetState, info.level, {stats: info.targetStats, priority: info.priority || 0, filter: info.filter});
    }
  }
  if (info.targets && info.targetState) {
    for (const targetId of info.targets) {
      addAura(targetId, info.targetState, info.level, {stats: info.targetStats, priority: info.priority || 0});
    }
  }
}

function _applyBuffs(buffs, stats, info, unit, team, skill) {
  const auras = {};
  applyAuras(auras, stats, unit, team, `skill${skill}`, info);
  if (!Object.keys(auras).length) return buffs;

  buffs = {...buffs};
  for (const [target, states] of Object.entries(auras)) {
    if (!buffs[target]) buffs[target] = [];
    for (const {state, stats: statsFunc, level, filter} of Object.values(states).sort((a, b) => b.priority - a.priority)) {
      const info = Data.states[state];
      buffs[target] = buffs[target].filter(buff => {
        if (buff.state === state) return false;
        if (info && (info.group || info.curse)) {
          const oinfo = Data.states[buff.state];
          if (oinfo && ((info.group && info.group === oinfo.group) || (info.curse && oinfo.curse))) {
            return false;
          }
        }
        return true;
      });
      buffs[target].push({
        state,
        skill,
        level,
        filter,
        stats: statsFunc(),
        source: unit,
        sourceName: stats[unit]?.name,
      });
    }
  }
  return buffs;
}

export function applyExternalSkillBuff(buffs, stats, id, level, team) {
  const skill = Data.skills[id];
  if (!skill) return buffs;
  const func = skillFunctions[skill.srvdofunc || 0];
  if (!func) return buffs;
  const unit = skill.srvdofunc === 18 ? (team === 0 ? 'player' : 'monster') : null;
  const tstats = unit ? stats[unit] : ({base: {}, skills: {}, weapon1: {}});
  const info = (func.buff || func.aura)?.call(func, id, tstats, level);
  if (!info) return buffs;

  return _applyBuffs(buffs, stats, info, unit, team, id);
}

export function applyExternalShrineBuff(buffs, stats, id) {
  const shrine = shrineBuffs[id];
  if (!shrine || !shrine.stats || !Data.stateByName[id]) return buffs;

  const info = {
    level: 1,
    filter: AuraFlags.FindPlayers | AuraFlags.TargetAllies,
    targetState: Data.stateByName[id].id,
    targetStats: () => shrine.stats,
  };

  return _applyBuffs(buffs, stats, info, null, 0, id);
}

export function applyBuffs(buffs, stats, unit, id, level, targets) {
  if (!stats[unit]) return buffs;
  const skill = Data.skills[id];
  if (!skill) return buffs;
  const info = skillFunctions[skill.srvdofunc || 0]?.buff?.(id, stats[unit], level, targets);
  if (!info) return buffs;

  return _applyBuffs(buffs, stats, info, unit, stats[unit].team, id);
}

class ItemStats extends StatObject {
  static keepStats = new Set(['strength', 'item_strength_perlevel', 'dexterity', 'item_dexterity_perlevel', 'energy', 'item_energy_perlevel', 'vitality', 'item_vitality_perlevel']);
  keep(id) {
    return ItemStats.keepStats.has(id);
  }
  finalize(slot, item, charData, dest) {
    this.add_perlevel('strength', 'item_strength_perlevel', charData.level, 3);
    this.add_perlevel('dexterity', 'item_dexterity_perlevel', charData.level, 3);
    this.add_perlevel('energy', 'item_energy_perlevel', charData.level, 3);
    this.add_perlevel('vitality', 'item_vitality_perlevel', charData.level, 3);

    if (this.energy) dest.add('energy', this.energy, 0, 0, slot);
    if (this.vitality) dest.add('vitality', this.vitality, 0, 0, slot);
  }

  requirements(item) {
    const base = Data.items[item.base];
    let reqstr = base.reqstr || 0, reqdex = base.reqdex || 0;
    if (this.item_req_percent) {
      reqstr += (reqstr * this.item_req_percent / 100) | 0;
      reqdex += (reqdex * this.item_req_percent / 100) | 0;
    }
    if (item.ethereal) {
      reqstr -= 10;
      reqdex -= 10;
    }
    return {reqstr, reqdex, addstr: this.strength || 0, adddex: this.dexterity || 0};
  }
}

class WeaponStats extends ItemStats {
  static keepStats = null;
  keep(id) {
    return WeaponStats.keepStats.has(id) || id === 'item_req_percent' || super.keep(id);
  }
  finalize(slot, item, charData, dest) {
    const base = Data.items[item.base];
    let mindam = base.mindam || 0;
    let maxdam = base.maxdam || 0;
    let minmisdam = base.minmisdam || 0;
    let maxmisdam = base.maxmisdam || 0;
    if (base['2handed'] && !(base['1or2handed'] && (charData.dualwield || charData.shieldbase))) {
      this.twohanded = true;
      mindam = base['2handmindam'] || 0;
      maxdam = base['2handmaxdam'] || 0;
    }

    if (item.ethereal) {
      mindam += (mindam >> 1);
      maxdam += (maxdam >> 1);
      minmisdam += (minmisdam >> 1);
      maxmisdam += (maxmisdam >> 1);
    }

    if (charData.mercDoubleDamage) {
      mindam *= 2;
      maxdam *= 2;
    }

    const base_maxdam = maxdam, base_maxmisdam = maxmisdam;
    if (this.item_mindamage_percent) {
      mindam += (mindam * this.item_mindamage_percent / 100) | 0;
      minmisdam += (minmisdam * this.item_mindamage_percent / 100) | 0;
      delete this.item_mindamage_percent;
    }
    if (this.item_maxdamage_percent) {
      maxdam += (maxdam * this.item_maxdamage_percent / 100) | 0;
      maxmisdam += (maxmisdam * this.item_maxdamage_percent / 100) | 0;
      delete this.item_maxdamage_percent;
    }
    if (this.item_maxdamage_percent_perlevel) {
      const pct = ((this.item_maxdamage_percent_perlevel * charData.level) >> 3);
      maxdam += (base_maxdam * pct / 100) | 0;
      maxmisdam += (base_maxmisdam * pct / 100) | 0;
      delete this.item_maxdamage_percent_perlevel;
    }

    if (this.item_maxdamage_perlevel) {
      this.maxdamage = (this.maxdamage || 0) + ((this.item_maxdamage_perlevel * charData.level) >> 3);
      delete this.item_maxdamage_perlevel;
    }

    if (minmisdam) {
      this.item_throw_mindamage = (this.mindamage || 0) + minmisdam;
      this.sources.item_throw_mindamage = {weapon: this.item_throw_mindamage};
    }
    if (maxmisdam) {
      this.item_throw_maxdamage = (this.maxdamage || 0) + maxmisdam;
      this.sources.item_throw_maxdamage = {weapon: this.item_throw_maxdamage};
    }
    this.mindamage = (this.mindamage || 0) + mindam;
    this.maxdamage = (this.maxdamage || 0) + maxdam;
    this.sources.mindamage = {weapon: this.mindamage};
    this.sources.maxdamage = {weapon: this.maxdamage};

    super.finalize(slot, item, charData, dest);
  }
}

class ArmorStats extends ItemStats {
  static keepStats = new Set(['armorclass', 'item_armor_perlevel', 'item_armor_percent', 'item_armorpercent_perlevel']);
  keep(id) {
    return ArmorStats.keepStats.has(id) || id === 'item_req_percent' || super.keep(id);
  }
  finalize(slot, item, charData, dest) {
    let defense = item.defense || 0;
    if (item.ethereal) defense += (defense >> 1);
    if (item.ethbugged) defense += (defense >> 1);
    const base = defense;
    if (this.item_armor_percent) {
      defense += (base * this.item_armor_percent / 100) | 0;
    }
    if (this.item_armorpercent_perlevel) {
      defense += (base * ((this.item_armorpercent_perlevel * charData.level) >> 3) / 100) | 0;
    }
    if (this.armorclass) {
      defense += this.armorclass;
    }
    if (this.item_armor_perlevel) {
      defense += (this.item_armor_perlevel * charData.level) >> 3;
    }
    dest.add('armorclass', defense, 0, 0, slot);
    super.finalize(slot, item, charData, dest);
  }
}

const junkStats = new Set(['item_ethereal', 'maxdurability', 'item_maxdurability_percent', 'item_replenish_durability',
  'item_replenish_quantity', 'item_extra_stack', 'item_indesctructible', 'item_numsockets']);

const weaponMastery = new Set(['passive_mastery_melee_th', 'passive_mastery_melee_dmg', 'passive_mastery_melee_crit',
  'passive_mastery_throw_th', 'passive_mastery_throw_dmg', 'passive_mastery_throw_crit']);

const resistances = new Set(['fireresist', 'coldresist', 'lightresist', 'poisonresist', 'magicresist', 'damageresist']);

function statsBuilder(charData, state) {
  const weapons = {};
  const char_base = new StatObject();
  const weapon_base = new StatObject();
  const sets = {};

  const { items, inventory, stats: baseStats, skills: baseSkills, ...result } = charData;

  result.base_skills = baseSkills || {};
  result.base = char_base;

  let numWeapons = 0, weapTwohand = false;
  if (items) {
    for (const [slot, id] of Object.entries(items)) {
      const item = (typeof id === 'object' ? id : (state.items[id] || presetItems[id]));
      const base = Data.items[item?.base];
      if (!base) continue;

      const types = itemGetTypes(item.base);
      if (types.has('weap')) {
        numWeapons += 1;
        if (base['2handed']) {
          weapTwohand = true;
        }
      } else if (types.has('shld')) {
        result.shieldbase = item.base;
        result.shieldslot = slot;
      } else if (types.has('boot')) {
        result.bootbase = item.base;
        result.bootslot = slot;
      }

      if (item?.quality !== Quality.SET) continue;
      const uniq = Data.setItems[item.unique];
      if (!uniq) continue;

      if (!sets[uniq.set]) {
        const ids = Object.keys(Data.setItems).filter(id => Data.setItems[id].set === uniq.set).sort().reverse();
        sets[uniq.set] = {ids, equipped: ids.map(() => false), count: 0};
      }
      const info = sets[uniq.set];
      const index = info.ids.indexOf(item.unique);
      if (index >= 0 && !info.equipped[index]) {
        info.count += 1;
        info.equipped[index] = true;
      }
    }
  }

  if (numWeapons > 1) {
    result.dualwield = true;
  }

  function addStats(stats, localStats, source, itype) {
    for (const idStr in stats) {
      let [id, param, param2] = idStr.split('#');
      let value = stats[idStr];
      if (typeof value === 'function') value = value();
      if (param2 === 'x') {
        const tmp = param;
        param = value;
        value = parseInt(tmp);
      } else if (Data.itemStatCost[id]?.descfunc === 15) {
        const tmp = param2;
        param2 = value;
        value = parseInt(tmp);
      }

      if (result.mercDoubleDamage && (id === 'mindamage' || id === 'maxdamage')) {
        value *= 3;
      }

      if (junkStats.has(id)) continue;
      if (localStats && localStats.keep(id)) {
        localStats.add(id, value, param, param2, source);
      } else if (WeaponStats.keepStats.has(id)) {
        weapon_base.add(id, value, param, param2, source);
      } else if (!localStats && weaponMastery.has(id)) {
        if (weapons.rarm && (!itype || isSubType(Data.items[weapons.rarm.baseitem]?.type, itype))) {
          weapons.rarm.add(id, value, param, param2, source);
        }
        if (weapons.larm && (!itype || isSubType(Data.items[weapons.larm.baseitem]?.type, itype))) {
          weapons.larm.add(id, value, param, param2, source);
        }
      } else if (resistances.has(id) && value < 0) {
        char_base.add(`negative_${id}`, -value, param, param2, source);
      } else {
        char_base.add(id, value, param, param2, source);
      }
    }
  }

  for (const source in baseStats) {
    addStats(baseStats[source], null, source);
  }
  // do this after adding base stats so base merc damage isn't tripled
  if (charData.unit === 'mercenary') {
    const merc = Data.hireling[charData.class]?.[0]?.class;
    if ((merc === 560 || merc === 561) && !weapTwohand) {
      result.mercDoubleDamage = true;
    }
  }

  const base_vit = char_base.vitality;
  const base_int = char_base.energy;
  const base_dex = char_base.dexterity;

  let armor_penalty = 0;

  let requirements = {};
  if (items) {
    for (const [slot, id] of Object.entries(items)) {
      const item = (typeof id === 'object' ? id : (state.items[id] || presetItems[id]));
      const base = Data.items[item?.base];
      if (!base) continue;

      const types = itemGetTypes(item.base);

      let localStats = null;
      if (types.has('weap')) {
        localStats = weapons[slot] = new WeaponStats();
        localStats.baseitem = item.base;
      } else if (types.has('armo')) {
        localStats = new ArmorStats();
        armor_penalty += base.speed;
        char_base.add('velocitypercent', -base.speed, 0, 0, slot);
      }

      if (item.stats) addStats(item.stats, localStats, slot);
      if (item.quality === Quality.SET) {
        const uniq = Data.setItems[item.unique];
        const info = sets[uniq?.set];
        if (info) {
          for (const {stats} of itemsetPartStats(uniq, info.equipped.slice(), info.ids.indexOf(item.unique), item)) {
            addStats(stats, localStats, slot);
          }
        }
      }
      if (item.socketedItems) {
        const stats = {};
        for (const sid of item.socketedItems) {
          if (Data.gems[sid]) {
            gemStats(stats, sid, types);
          } else if (state.items[sid]?.stats) {
            addStats(state.items[sid]?.stats, localStats, slot);
          }
        }
        addStats(stats, localStats, slot);
      }

      if (localStats) {
        localStats.finalize(slot, item, result, char_base);
        const req = localStats.requirements(item);
        if (result.unit === 'player' || result.unit === 'mercenary') {
          requirements[slot] = req;
        } else {
          if (req.addstr) char_base.add('strength', req.addstr, 0, 0, slot);
          if (req.adddex) char_base.add('dexterity', req.adddex, 0, 0, slot);
        }
      }
    }
  }

  if (inventory) {
    inventory.forEach((id, index) => {
      const item = state.items[id] || presetItems[id];
      const base = Data.items[item?.base];
      if (!base || !isSubType(base.type, 'char')) return;
      if (Data.info.pd2 && index < 40) return; // little hack
      if (item.stats) addStats(item.stats, null, `inventory${index}`);
    });
  }

  result.reqstr = 0;
  result.reqdex = 0;
  while (Object.keys(requirements).length) {
    const remaining = {};
    for (const [slot, req] of Object.entries(requirements)) {
      result.reqstr = Math.max(result.reqstr, req.reqstr);
      result.reqdex = Math.max(result.reqdex, req.reqdex);
      if (req.reqstr <= char_base.strength && req.reqdex <= char_base.dexterity) {
        if (req.addstr) char_base.add('strength', req.addstr, 0, 0, slot);
        if (req.adddex) char_base.add('dexterity', req.adddex, 0, 0, slot);
      } else {
        remaining[slot] = req;
      }
    }
    if (Object.keys(remaining).length >= Object.keys(requirements).length) {
      break;
    } else {
      requirements = remaining;
    }
  }
  result.equipstr = char_base.strength;
  result.equipdex = char_base.dexterity;
  let finalstr = char_base.strength;
  let finaldex = char_base.dexterity;
  while (Object.keys(requirements).length) {
    let nstr = Infinity, ndex = Infinity;
    for (const req of Object.values(requirements)) {
      const rstr = Math.max(0, req.reqstr - finalstr);
      const rdex = Math.max(0, req.reqdex - finaldex);
      if (rdex < ndex || (rdex === ndex && rstr < nstr)) {
        nstr = rstr;
        ndex = rdex;
      }
    }
    result.equipstr += nstr;
    result.equipdex += ndex;
    finalstr += nstr;
    finaldex += ndex;
    const remaining = {};
    for (const [slot, req] of Object.entries(requirements)) {
      if (req.reqstr <= finalstr && req.reqdex <= finaldex) {
        finalstr += req.addstr || 0;
        finaldex += req.adddex || 0;
      } else {
        remaining[slot] = req;
      }
    }
    requirements = remaining;
  }

  for (const [set, info] of Object.entries(sets)) {
    addStats(itemsetStats({}, Data.sets[set], info.count, info.ids.length), null, `set.${set}`);
  }

  char_base.add('armorclass', Math.floor(char_base.dexterity / 4), 0, 0, 'stats');

  if (result.unit === 'player' || result.unit === 'mercenary') {
    const penalty = Data.difficulty[result.difficulty].resistpenalty || 0;
    char_base.add('fireresist', penalty, 0, 0, 'difficulty');
    char_base.add('lightresist', penalty, 0, 0, 'difficulty');
    char_base.add('coldresist', penalty, 0, 0, 'difficulty');
    char_base.add('poisonresist', penalty, 0, 0, 'difficulty');
    char_base.add('item_poisonlengthresist', penalty, 0, 0, 'difficulty');
  }

  const buffs = state.buffs?.[result.unit];
  if (buffs) {
    for (const {state, skill, filter, stats, level} of buffs) {
      if (filter && (!(filter & result.aura_include) || (filter & result.aura_exclude))) continue;
      const source = (shrineBuffs[skill] ? skill : `skill${skill}`);
      char_base.add('state', level || 1, state, 0, source);
      addStats(stats, null, source);
    }
  }

  result.skills = new StatObject();
  for (const id in result.base_skills) {
    result.skills.add(id, result.base_skills[id], 0, 0, 'base');
  }
  if (char_base.item_nonclassskill) {
    for (const id in char_base.item_nonclassskill) {
      let level = char_base.item_nonclassskill[id];
      if (Data.skills[id]?.charclass === result.class) {
        level = Math.min(level, 3);
      }
      result.skills.add(id, level, 0, 0, char_base.sources[`item_nonclassskill#${id}`]);
      result.skills._addSource(id, level, `item_nonclassskill#${id}`);
    }
  }
  if (char_base.item_singleskill) {
    for (const id in char_base.item_singleskill) {
      const level = char_base.item_singleskill[id];
      if (result.skills[id] || Data.skills[id]?.charclass === result.class) {
        result.skills.add(id, level, 0, 0, char_base.sources[`item_singleskill#${id}`]);
        result.skills._addSource(id, level, `item_singleskill#${id}`);
      }
    }
  }
  if (char_base.item_elemskill) {
    for (const type in char_base.item_elemskill) {
      const elem = [null, 'fire', 'ltng', 'mag', 'cold', 'pois', 'life', 'mana'][type];
      const level = char_base.item_elemskill[type];
      const sources = char_base.sources[`item_elemskill#${type}`];
      for (const id in result.skills) {
        if (Data.skills[id]?.etype === elem) {
          result.skills.add(id, level, 0, 0, sources);
          result.skills._addSource(id, level, `item_elemskill#${type}`);
        }
      }
    }
  }
  if (char_base.item_addskill_tab) {
    for (let tab in char_base.item_addskill_tab) {
      const level = char_base.item_addskill_tab[tab];
      const sources = char_base.sources[`item_addskill_tab#${tab}`];
      tab = parseInt(tab);
      if (Data.classes[(tab / 3) | 0] !== (result.skillClass || result.class)) continue;
      const page = (tab % 3) + 1;
      for (const id in result.skills) {
        const info = Data.skills[id];
        if (info?.charclass === (result.skillClass || result.class) && Data.skillDesc[info?.skilldesc]?.skillpage === page) {
          result.skills.add(id, level, 0, 0, sources);
          result.skills._addSource(id, level, `item_addskill_tab#${tab}`);
        }
      }
    }
  }
  if (char_base.item_addclassskills) {
    const index = Data.classes.indexOf(result.skillClass || result.class);
    const level = char_base.item_addclassskills[index];
    if (level) {
      const sources = char_base.sources[`item_addclassskills#${index}`];
      for (const id in result.skills) {
        if (Data.skills[id]?.charclass === (result.skillClass || result.class)) {
          result.skills.add(id, level, 0, 0, sources);
          result.skills._addSource(id, level, `item_addclassskills#${index}`);
        }
      }
    }
  }
  if (char_base.item_allskills) {
    const level = char_base.item_allskills;
    const sources = char_base.sources.item_allskills;
    for (const id in result.skills) {
      result.skills.add(id, level, 0, 0, sources);
      result.skills._addSource(id, level, 'item_allskills');
    }
  }

  // auras are processed before finalize()

  function finalize() {
    result.skills.add(0, 1, 0, 0, 'base');
    if (result.unit === 'player') {
      // throw, left hand throw
      result.skills.add(2, 1, 0, 0, 'base');
      result.skills.add(4, 1, 0, 0, 'base');
    }
  
    const weapon1 = (weapons.rarm || weapons.larm);
    const weapon2 = (weapons.rarm && weapons.larm);
    // remember weapon IAS for wereform calculations
    if (weapon1) weapon1.weaponias = weapon1.item_fasterattackrate;
    if (weapon2) weapon2.weaponias = weapon2.item_fasterattackrate;
    // apply min/max damage to throws
    if (weapon_base.mindamage) {
      weapon_base.item_throw_mindamage = weapon_base.mindamage;
      weapon_base.sources.item_throw_mindamage = {...weapon_base.sources.mindamage};
    }
    if (weapon_base.maxdamage) {
      weapon_base.item_throw_maxdamage = weapon_base.maxdamage;
      weapon_base.sources.item_throw_maxdamage = {...weapon_base.sources.maxdamage};
    }
    // merge weapon stats with attack stats from other items
    if (weapon2) {
      result.weapon2 = clone(weapon_base, true);
      result.weapon2.copy(weapon2);
    }
    result.weapon1 = weapon_base;
    if (weapon1) {
      result.weapon1.copy(weapon1);
    } else if (result.unit === 'player') {
      result.weapon1.add('mindamage', 1, 0, 0, 'base');
      result.weapon1.add('maxdamage', 2, 0, 0, 'base');
    }

    // calculate WSM
    if (result.weapon1) {
      const base = Data.items[result.weapon1.baseitem];
      result.weapon1.weaponspeed = base?.speed || 0;
    }
    if (result.weapon2) {
      const base = Data.items[result.weapon2.baseitem];
      result.weapon2.weaponspeed = base?.speed || 0;
    }
    if (result.weapon1 && result.weapon2) {
      const wsm1 = result.weapon1.weaponspeed, wsm2 = result.weapon2.weaponspeed;
      result.weapon1.weaponspeed = (wsm1 + wsm2) / 2 | 0;
      if (Data.info.patch) {
        result.weapon2.weaponspeed = result.weapon1.weaponspeed;
      } else {
        result.weapon2.weaponspeed = result.weapon1.weaponspeed - wsm1 + wsm2;
        if (result.swapweapons) {
          const tmp = result.weapon1;
          result.weapon1 = result.weapon2;
          result.weapon1 = tmp;
        }
      }
    }
  
    // apply percentage and per level bonuses
    char_base.add_percent('maxhp', 'item_maxhp_percent');
    char_base.add_percent('maxmana', 'item_maxmana_percent');
    const maxstamina = char_base.maxstamina || 0;
    char_base.add_func('maxstamina', 'skill_staminapercent', value => (maxstamina * value / 100) | 0);
    char_base.add_func('maxstamina', 'skill_passive_staminapercent', value => (maxstamina * value / 100) | 0);

    char_base.add_perlevel('maxhp', 'item_hp_perlevel', result.level, 3);
    char_base.add_perlevel('maxmana', 'item_mana_perlevel', result.level, 3);

    char_base.add_perlevel('coldresist', 'item_resist_cold_perlevel', result.level, 3);
    char_base.add_perlevel('fireresist', 'item_resist_fire_perlevel', result.level, 3);
    char_base.add_perlevel('lightresist', 'item_resist_ltng_perlevel', result.level, 3);
    char_base.add_perlevel('poisonresist', 'item_resist_pois_perlevel', result.level, 3);

    char_base.add_perlevel('item_absorbcold', 'item_absorb_cold_perlevel', result.level, 3);
    char_base.add_perlevel('item_absorbfire', 'item_absorb_fire_perlevel', result.level, 3);
    char_base.add_perlevel('item_absorblight', 'item_absorb_ltng_perlevel', result.level, 3);
    char_base.add_perlevel('item_absorbmagic', 'item_absorb_pois_perlevel', result.level, 3); // ??
    if (char_base.item_absorbfire_percent > 40) char_base.item_absorbfire_percent = 40;
    if (char_base.item_absorblight_percent > 40) char_base.item_absorblight_percent = 40;
    if (char_base.item_absorbmagic_percent > 40) char_base.item_absorbmagic_percent = 40;
    if (char_base.item_absorbcold_percent > 40) char_base.item_absorbcold_percent = 40;

    char_base.add_perlevel('item_attackertakesdamage', 'item_thorns_perlevel', result.level, 3);
    char_base.add_perlevel('item_goldbonus', 'item_find_gold_perlevel', result.level, 3);
    char_base.add_perlevel('item_magicbonus', 'item_find_magic_perlevel', result.level, 3);
    char_base.add_perlevel('staminarecoverybonus', 'item_regenstamina_perlevel', result.level, 3);
    char_base.add_perlevel('maxstamina', 'item_stamina_perlevel', result.level, 3 + 8);

    char_base.add_perlevel('armorclass', 'item_armor_perlevel', result.level, 3);

    const ac = char_base.armorclass || 0;
    char_base.add_func('armorclass', 'item_armor_percent', value => (ac * value / 100) | 0);
    char_base.add_func('armorclass', 'skill_armor_percent', value => (ac * value / 100) | 0);
    char_base.add_percent('armorclass', 'armor_override_percent');

    const capped = (value, min, max) => Math.max(min, Math.min(max, value));

    // cap resists
    if (result.unit === 'player' || result.unit === 'mercenary') {
      char_base.add('maxmagicresist', 75);
      char_base.add('maxfireresist', 75);
      char_base.add('maxlightresist', 75);
      char_base.add('maxcoldresist', 75);
      char_base.add('maxpoisonresist', 75);
      if (char_base.maxmagicresist > 95) char_base.maxmagicresist = 95;
      if (char_base.maxfireresist > 95) char_base.maxfireresist = 95;
      if (char_base.maxlightresist > 95) char_base.maxlightresist = 95;
      if (char_base.maxcoldresist > 95) char_base.maxcoldresist = 95;
      if (char_base.maxpoisonresist > 95) char_base.maxpoisonresist = 95;

      for (const id of resistances) {
        char_base.add_direct(`display_${id}`, id);
        char_base.add_func(`display_${id}`, `negative_${id}`, value => -value);
      }

      result.full_damageresist = char_base.display_damageresist;
      char_base.display_damageresist = capped(char_base.display_damageresist || 0, -100, 50);
      result.full_magicresist = char_base.display_magicresist;
      char_base.display_magicresist = capped(char_base.display_magicresist || 0, -100, char_base.maxmagicresist);
      result.full_fireresist = char_base.display_fireresist;
      char_base.display_fireresist = capped(char_base.display_fireresist || 0, -100, char_base.maxfireresist);
      result.full_lightresist = char_base.display_lightresist;
      char_base.display_lightresist = capped(char_base.display_lightresist || 0, -100, char_base.maxlightresist);
      result.full_coldresist = char_base.display_coldresist;
      char_base.display_coldresist = capped(char_base.display_coldresist || 0, -100, char_base.maxcoldresist);
      result.full_poisonresist = char_base.display_poisonresist;
      char_base.display_poisonresist = capped(char_base.display_poisonresist || 0, -100, char_base.maxpoisonresist);
      char_base.item_poisonlengthresist = capped(char_base.item_poisonlengthresist || 0, -100, 75);
    } else {
      for (const id of resistances) {
        const display_id = `display_${id}`;
        char_base.add_direct(display_id, id);
        char_base.add_func(display_id, `negative_${id}`, value => char_base[display_id] >= 100 ? (-value / 5) | 0 : -value);
        char_base[display_id] = Math.max(-100, char_base[display_id] || 0);
      }
      char_base.item_poisonlengthresist = Math.max(-100, char_base.item_poisonlengthresist || 0);
    }

    function processWeapon(weapon) {
      // apply per level bonuses
      weapon.add('tohit', (char_base.dexterity - base_dex) * 5, 0, 0, 'stats');
      weapon.add_perlevel('tohit', 'item_tohit_perlevel', result.level, 1);

      weapon.add_perlevel('coldmaxdam', 'item_cold_damagemax_perlevel', result.level, 3);
      weapon.add_perlevel('firemaxdam', 'item_fire_damagemax_perlevel', result.level, 3);
      weapon.add_perlevel('lightmaxdam', 'item_ltng_damagemax_perlevel', result.level, 3);
      weapon.add_perlevel('poisonmaxdam', 'item_pois_damagemax_perlevel', result.level, 3);

      weapon.add_perlevel('item_demondamage_percent', 'item_damage_demon_perlevel', result.level, 3);
      weapon.add_perlevel('item_undeaddamage_percent', 'item_damage_undead_perlevel', result.level, 3);
      weapon.add_perlevel('item_demon_tohit', 'item_tohit_demon_perlevel', result.level, 1);
      weapon.add_perlevel('item_undead_tohit', 'item_tohit_undead_perlevel', result.level, 1);
      weapon.add_perlevel('item_tohit_percent', 'item_tohitpercent_perlevel', result.level, 1);
      weapon.add_perlevel('item_crushingblow', 'item_crushingblow_perlevel', result.level, 3);
      weapon.add_perlevel('item_openwounds', 'item_openwounds_perlevel', result.level, 3);
      weapon.add_perlevel('item_kickdamage', 'item_kick_damage_perlevel', result.level, 3);
      weapon.add_perlevel('item_deadlystrike', 'item_deadlystrike_perlevel', result.level, 3);
      weapon.add_perlevel('maxdamage', 'item_maxdamage_perlevel', result.level, 3);
      weapon.add_perlevel('item_throw_maxdamage', 'item_maxdamage_perlevel', result.level, 3);

      weapon.add_direct('damagepercent', 'item_mindamage_percent');

      const item = Data.items[weapon.baseitem];
      const throwpot = item && isSubType(item.type, 'tpot');

      // Venom poison length override
      if (char_base.skill_poison_override_length) {
        weapon.poisonlength = 0;
        weapon.sources.poisonlength = {};
        weapon.add('poisonlength', char_base.skill_poison_override_length, 0, 0, char_base.sources.skill_poison_override_length);
        weapon.poison_count = 1;
      }

      // add attribute scaling to mastery stats - it seems like the right place for them
      const ranged = item && isSubType(item.type, 'miss');
      const scaling = item ? ((char_base.strength * (item.strbonus || 0) / 100) | 0) + ((char_base.dexterity * (item.dexbonus || 0) / 100) | 0) : 0;
      weapon.add('passive_mastery_melee_dmg', item ? scaling : char_base.strength, 0, 0, 'stats');
      weapon.add('passive_mastery_throw_dmg', scaling, 0, 0, 'stats');

      const normaldamage = weapon.item_normaldamage || 0;

      // mastery ONLY applies to base damage for melee attacks
      if (!ranged && char_base.passive_fire_mastery) {
        if (weapon.firemindam) weapon.display_firemindam = d2_addpercent(weapon.firemindam << 8, char_base.passive_fire_mastery) >> 8;
        if (weapon.firemaxdam) weapon.display_firemaxdam = d2_addpercent(weapon.firemaxdam << 8, char_base.passive_fire_mastery) >> 8;
      }
      if (!ranged && char_base.passive_cold_mastery) {
        if (weapon.coldmindam) weapon.display_coldmindam = d2_addpercent(weapon.coldmindam << 8, char_base.passive_cold_mastery) >> 8;
        if (weapon.coldmaxdam) weapon.display_coldmaxdam = d2_addpercent(weapon.coldmaxdam << 8, char_base.passive_cold_mastery) >> 8;
      }
      if (!ranged && char_base.passive_ltng_mastery) {
        if (weapon.lightmindam) weapon.display_lightmindam = d2_addpercent(weapon.lightmindam << 8, char_base.passive_ltng_mastery) >> 8;
        if (weapon.lightmaxdam) weapon.display_lightmaxdam = d2_addpercent(weapon.lightmaxdam << 8, char_base.passive_ltng_mastery) >> 8;
      }
      if (!ranged && char_base.passive_pois_mastery) {
        if (weapon.poisonmindam) weapon.display_poisonmindam = d2_addpercent(weapon.poisonmindam << 8, char_base.passive_pois_mastery) >> 8;
        if (weapon.poisonmaxdam) weapon.display_poisonmaxdam = d2_addpercent(weapon.poisonmaxdam << 8, char_base.passive_pois_mastery) >> 8;
      }
      if (!ranged && char_base.passive_mag_mastery) {
        if (weapon.magicmindam) weapon.display_magicmindam = d2_addpercent(weapon.magicmindam << 8, char_base.passive_mag_mastery) >> 8;
        if (weapon.magicmaxdam) weapon.display_magicmaxdam = d2_addpercent(weapon.magicmaxdam << 8, char_base.passive_mag_mastery) >> 8;
      }

      // calculate displayed total damage and attack rating
      if (!item || !throwpot) {
        const mastery = (weapon.passive_mastery_melee_dmg || 0);
        weapon.display_mindamage = d2_addpercent(((weapon.mindamage || 0) + normaldamage) << 8, (weapon.damagepercent || 0) + mastery) >> 8;
        weapon.display_maxdamage = d2_addpercent(((weapon.maxdamage || 0) + normaldamage) << 8, (weapon.damagepercent || 0) + mastery) >> 8;
        if (!ranged && weapon.display_mindamage >= weapon.display_maxdamage && weapon.display_mindamage) {
          weapon.display_maxdamage = weapon.display_mindamage + 1;
        } else if (weapon.display_mindamage > weapon.display_maxdamage) {
          const tmp = weapon.display_maxdamage;
          weapon.display_maxdamage = weapon.display_mindamage;
          weapon.display_mindamage = tmp;
        }
        weapon.display_physmindam = weapon.display_mindamage;
        weapon.display_physmaxdam = weapon.display_maxdamage;
        weapon.display_mindamage += (weapon.display_firemindam || weapon.firemindam || 0) + (weapon.display_coldmindam || weapon.coldmindam || 0) +
          (weapon.display_lightmindam || weapon.lightmindam || 0) + (weapon.display_magicmindam || weapon.magicmindam || 0);
        weapon.display_maxdamage += (weapon.display_firemaxdam || weapon.firemaxdam || 0) + (weapon.display_coldmaxam || weapon.coldmaxdam || 0) +
          (weapon.display_lightmaxdam || weapon.lightmaxdam || 0) + (weapon.display_magicmaxdam || weapon.magicmaxdam || 0);
        const poison = (weapon.poisonlength || 0) / (weapon.poison_count || 1);
        if (poison) {
          weapon.display_mindamage += Math.round((weapon.display_poisonmindam || weapon.poisonmindam || 0) * poison / 256);
          weapon.display_maxdamage += Math.round((weapon.display_poisonmaxdam || weapon.poisonmaxdam || 0) * poison / 256);
        }
        weapon.display_tohit = d2_addpercent(weapon.tohit || 0, (weapon.item_tohit_percent || 0) + (weapon.passive_mastery_melee_th || 0));
        weapon.display_tohit = d2_addpercent(weapon.display_tohit, char_base.shrine_tohit_percent || 0);
      }

      if (item && (throwpot || item.maxmisdam)) {
        const mastery = (weapon.passive_mastery_throw_dmg || 0);
        weapon.display_throw_mindamage = d2_addpercent(((weapon.item_throw_mindamage || 0) + normaldamage) << 8, (weapon.damagepercent || 0) + mastery) >> 8;
        weapon.display_throw_maxdamage = d2_addpercent(((weapon.item_throw_maxdamage || 0) + normaldamage) << 8, (weapon.damagepercent || 0) + mastery) >> 8;
        if (weapon.display_throw_mindamage > weapon.display_throw_maxdamage) {
          const tmp = weapon.display_throw_maxdamage;
          weapon.display_throw_maxdamage = weapon.display_throw_mindamage;
          weapon.display_throw_mindamage = tmp;
        }
        weapon.display_throw_physmindam = weapon.display_throw_mindamage;
        weapon.display_throw_physmaxdam = weapon.display_throw_maxdamage;
        weapon.display_throw_mindamage += (weapon.firemindam || 0) + (weapon.coldmindam || 0) + (weapon.lightmindam || 0) + (weapon.magicmindam || 0);
        weapon.display_throw_maxdamage += (weapon.firemaxdam || 0) + (weapon.coldmaxdam || 0) + (weapon.lightmaxdam || 0) + (weapon.magicmaxdam || 0);
        const poison = (weapon.poisonlength || 0) / (weapon.poison_count || 1);
        if (poison) {
          weapon.display_throw_mindamage += Math.round((weapon.poisonmindam || 0) * poison / 256);
          weapon.display_throw_maxdamage += Math.round((weapon.poisonmaxdam || 0) * poison / 256);
        }

        weapon.display_throw_tohit = d2_addpercent(weapon.tohit || 0, (weapon.item_tohit_percent || 0) + (weapon.passive_mastery_throw_th || 0));
        weapon.display_throw_tohit = d2_addpercent(weapon.display_throw_tohit, char_base.shrine_tohit_percent || 0);
      } else {
        delete weapon.item_throw_mindamage;
        delete weapon.item_throw_maxdamage;
      }

      // calculate total attack rate
      const ias = weapon.item_fasterattackrate || 0;
      weapon.add_direct('display_attackrate', 'attackrate');
      weapon.add('display_attackrate', (ias * 120) / (ias + 120) | 0, 0, 0, 'items');
      weapon.add('display_attackrate', -weapon.weaponspeed, 0, 0, 'weapon');
    }

    if (result.weapon1) processWeapon(result.weapon1);
    if (result.weapon2) processWeapon(result.weapon2);

    // for players only, apply attribute scaling (it applies after % increases) and mana/stamina calcs
    if (result.unit === 'player') {
      const data = Data.charStats[result.class];

      char_base.add('maxhp', Math.floor((char_base.vitality - base_vit) * data.lifepervitality / 4), 0, 0, 'stats');
      char_base.add('maxstamina', Math.floor((char_base.vitality - base_vit) * data.staminapervitality / 4), 0, 0, 'stats');
      char_base.add('maxmana', Math.floor((char_base.energy - base_int) * data.manapermagic / 4), 0, 0, 'stats');

      result.staminadrain = 2 * data.rundrain * (1 + Math.floor(armor_penalty / 10));
      if (char_base.item_staminadrainpct) {
        result.staminadrain -= (result.staminadrain * char_base.item_staminadrainpct / 100) | 0;
      }
      result.staminaregen = char_base.maxstamina;
      if (char_base.staminarecoverybonus) {
        result.staminadrain += (result.staminaregen * char_base.staminarecoverybonus / 100) | 0;
      }

      result.manaregen = Math.floor(256 * char_base.maxmana / (25 * data.manaregen));
      if (char_base.manarecoverybonus) {
        result.manaregen += (result.manaregen * char_base.manarecoverybonus / 100) | 0;
      }

      function doubleChance(value) {
        if (value >= 200) {
          return 100 * (value - 101) / value;
        } else if (value) {
          return (value - 1) * (value - 1) / (4 * value);
        } else {
          return (value - 2) / 4;
        }
      }
      result.doubleheal = doubleChance(char_base.vitality);
      result.doublemana = doubleChance(char_base.energy);
    }

    // monster regen
    char_base.add_func('hpregen', 'damageregen', value => (value * char_base.maxhp / 16) | 0);

    // movement speed calculations
    result.chargevelocity = 22.5 * (1 + (char_base.velocitypercent || 0)) / 100;
    const frw = (char_base.velocitypercent || 0) + ((150 * (char_base.item_fastermovevelocity || 0) / (150 + (char_base.item_fastermovevelocity || 0))) | 0);
    if (result.walkvelocity && frw) {
      const bonus = frw * result.walkvelocity / 100;
      result.walkvelocity += bonus;
      result.runvelocity += bonus;
    }

    // block chance
    if (result.shieldbase) {
      char_base.add('toblock', Data.items[result.shieldbase].block, 0, 0, 'base');
    }
    result.blockchance = 0;
    if (result.unit === 'player' || result.unit === 'mercenary') {
      if (result.shieldbase) {
        result.blockchance = (char_base.toblock || 0) * (char_base.dexterity - 15) / (result.level * 2)
      }
    } else {
      result.blockchance = char_base.toblock || 0;
    }
    result.blockchance = Math.min(result.blockchance, 75);

    return result;
  }

  return {stats: result, finalize, add: (stats, source, itype) => addStats(stats, null, source, itype)};
}

function questStats(quests) {
  const result = {};
  for (const qt of quests) {
    if (qt.thegoldenbird) {
      result.maxhp = (result.maxhp || 0) + 20;
    }
    if (qt.prisonofice) {
      result.fireresist = (result.fireresist || 0) + 10;
      result.lightresist = (result.lightresist || 0) + 10;
      result.coldresist = (result.coldresist || 0) + 10;
      result.poisonresist = (result.poisonresist || 0) + 10;
    }
  }
  return result;
}

function computeStats(state) {
  if (!WeaponStats.keepStats) {
    WeaponStats.keepStats = new Set(['item_magicarrow', 'item_explosivearrow']);
    for (const id in Data.itemStatCost) {
      if (id !== '' && Data.itemStatCost[id].damagerelated) {
        WeaponStats.keepStats.add(id);
      }
    }
    WeaponStats.keepStats.delete('item_staminadrainpct');
  }

  const profile = state.profiles[state.activeProfile];

  const builders = {};

  const charStats = Data.charStats[profile.class];
  const items = {...profile.items};
  if (profile.weaponSet) {
    items.rarm = items.rarm2;
    items.larm = items.larm2;
  }
  delete items.rarm2;
  delete items.larm2;
  builders.player = statsBuilder({
    unit: 'player',
    name: Locale.strings[Data.classNames[profile.class]],
    team: 0,
    level: profile.level,
    class: profile.class,
    difficulty: profile.difficulty,
    items,
    inventory: profile.inventory,
    skills: profileSelectSkills(profile),
    aura_include: AuraFlags.FindPlayers,
    aura_exclude: AuraFlags.OnlyUndead | AuraFlags.OnlyCorpses,
    walkvelocity: charStats.walkvelocity,
    runvelocity: charStats.runvelocity,
    swapweapons: profile.active.swapweapons, // 'wrong' order dual wielding
    size: 2,
    stats: {
      base: {
        drain: 100,
        coldeffect: -50,
        strength: charStats.str,
        dexterity: charStats.dex,
        energy: charStats.int,
        vitality: charStats.vit,
        maxhp: charStats.hpadd + Math.floor((profile.level - 1) * charStats.lifeperlevel / 4) + charStats.vit,
        maxstamina: charStats.stamina + Math.floor((profile.level - 1) * charStats.staminaperlevel / 4),
        maxmana: charStats.int + Math.floor((profile.level - 1) * charStats.manaperlevel / 4),
        toblock: charStats.blockfactor,
        tohit: (charStats.dex - 7) * 5 + charStats.tohitfactor,
      },
      stats: {
        strength: profile.stats.str,
        dexterity: profile.stats.dex,
        energy: profile.stats.int,
        vitality: profile.stats.vit,
        maxhp: Math.floor(profile.stats.vit * charStats.lifepervitality / 4),
        maxstamina: Math.floor(profile.stats.vit * charStats.staminapervitality / 4),
        maxmana: Math.floor(profile.stats.int * charStats.manapermagic / 4),
        tohit: profile.stats.dex * 5,
      },
      quest: questStats(profile.quests),
    },
  }, state);

  if (profile.merc && Data.hireling[profile.merc]?.length) {
    const level = Math.max(2, profile.mercLevel || Math.min(profile.level, 98));
    const lines = Data.hireling[profile.merc];
    let data = lines[0];
    for (const line of lines) {
      if (line.level <= level) {
        data = line;
      }
    }
    const delta = level - data.level;
    const skills = {};
    if (data.skill1) skills[data.skill1] = data.level1 + ((data.lvlperlvl1 * delta / 32) | 0);
    if (data.skill2) skills[data.skill2] = data.level2 + ((data.lvlperlvl2 * delta / 32) | 0);
    if (data.skill3) skills[data.skill3] = data.level3 + ((data.lvlperlvl3 * delta / 32) | 0);
    const monster = Data.monsterById[data.class];
    const resist = data.resist + ((delta * data.resistlvl / 4) | 0);

    const i1 = Locale.stringi[data.namefirst];
    const i2 = Locale.stringi[data.namelast];
    const name = Locale.istrings[i1 && i2 && i1 + ((profile.mercName || 0) % (i2 - i1 + 1))] || Locale.strings[data.namefirst];

    let skillClass = null;
    if (Data.info.patch) {
      if (data.class === 271) skillClass = 'ama';
      if (data.class === 560 || data.class === 561) skillClass = 'bar';
    }

    builders.mercenary = statsBuilder({
      unit: 'mercenary',
      name,
      team: 0,
      level,
      class: profile.merc,
      skillClass,
      items: profile.mercItems,
      difficulty: profile.difficulty,
      skills,
      walkvelocity: monster?.velocity,
      runvelocity: monster?.run,
      aura_include: AuraFlags.FindMonsters,
      aura_exclude: AuraFlags.OnlyUndead | AuraFlags.OnlyCorpses,
      size: 2,
      stats: {
        base: {
          drain: 100,
          coldeffect: -50,
          damageregen: monster?.damageregen || 0,
          crit: monster?.crit || 0,
          maxhp: Math.max(40, data.hp + delta * data.hplvl),
          armorclass: Math.max(0, data.defense + delta * data.deflvl),
          strength: data.str + ((delta * data.strlvl / 8) | 0),
          dexterity: data.dex + ((delta * data.dexlvl / 8) | 0),
          tohit: Math.max(0, data.ar + delta * data.arlvl),
          mindamage: Math.max(0, data.dmgmin + ((delta * data.dmglvl / 8) | 0)),
          maxdamage: Math.max(1, data.dmgmax + ((delta * data.dmglvl / 8) | 0)),
          fireresist: resist,
          lightresist: resist,
          coldresist: resist,
          poisonresist: resist,
        },
        stats: {
          tohit: Math.max(0, ((delta * data.dexlvl / 8) | 0) * 5),
        },
        player: {
          item_magicbonus: (builders.player.stats.base.item_magicbonus || 0) + ((builders.player.stats.base.item_find_magic_perlevel || 0) * profile.level >> 3),
          item_goldbonus: (builders.player.stats.base.item_goldbonus || 0) + ((builders.player.stats.base.item_find_gold_perlevel || 0) * profile.level >> 3),
        }
      },
    }, state);
  }

  if (state.summons) {
    const player = builders.player.stats;
    const summonSkills = new Set([...Object.keys(player.skills)]);
    for (const domain of [player.base, player.weapon1, player.weapon2]) {
      if (!domain) continue;
      for (const stat of [domain.item_charged_skill, domain.item_skillonattack, domain.item_skillonkill, domain.item_skillondeath,
          domain.item_skillonhit, domain.item_skillonlevelup, domain.item_skillongethit]) {
        if (!stat) continue;
        for (const id in stat) {
          const p = id.split('#');
          summonSkills.add(p[0]);
        }
      }
    }
    for (const summon of Object.values(state.summons)) {
      if (summon.skill && !summonSkills.has(summon.skill)) continue;
      const stats = summonStats(summon, profile, state);
      if (stats) {
        builders[stats.unit] = statsBuilder(stats, state);
      }
    }
  }

  const monster = selectMonster(profile);
  builders.monster = statsBuilder(monsterStats(monster, profile.difficulty, profile.level), state);

  const results = {};
  for (const id in builders) {
    results[id] = builders[id].stats;
  }

  function handleAuras(id, stats, itemLevel) {
    const skill = Data.skills[id];
    if (!skill) return;
    const aura = skillFunctions[skill.srvdofunc || 0]?.aura?.(id, stats, itemLevel || stats.skills[id], profile.active[`${stats.unit}${id}`], !!itemLevel);
    if (aura) applyAuras(auras, results, stats.unit, stats.team, `skill${id}`, aura);
  }

  const auras = {};
  for (const {stats} of Object.values(builders)) {
    for (const id in stats.skills) {
      handleAuras(id, stats);
    }
    if (stats.base.item_aura) {
      for (const id in stats.base.item_aura) {
        handleAuras(id, stats, stats.base.item_aura[id]);
      }
    }
  }
  for (const [unit, states] of Object.entries(auras)) {
    for (const {state, level, stats, source, itemtype} of Object.values(states).sort((a, b) => b.priority - a.priority)) {
      const statObject = {[`state#${state}`]: level, ...stats(builders[unit].stats)};
      if (Data.states[state]?.sunderresreduce) {
        if (statObject.passive_fire_pierce) statObject.passive_fire_pierce_reduce = statObject.passive_fire_pierce;
        if (statObject.passive_ltng_pierce) statObject.passive_ltng_pierce_reduce = statObject.passive_ltng_pierce;
        if (statObject.passive_cold_pierce) statObject.passive_cold_pierce_reduce = statObject.passive_cold_pierce;
        if (statObject.passive_pois_pierce) statObject.passive_pois_pierce_reduce = statObject.passive_pois_pierce;
      }
      builders[unit].add(statObject, source, itemtype);
    }
  }

  for (const id in builders) {
    builders[id].finalize();
  }
  return results;
}

const memoCompute = memoizedCompute(computeStats);

const _embedCache = new Map();
export function selectStats(state) {
  if (isEmbed()) {
    if (!_embedCache.has(state)) _embedCache.set(state, computeStats(state));
    return _embedCache.get(state);
  } else {
    return memoCompute(state);
  }
}
